import { Meeting } from './meeting';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DatabaseRecord } from '../mixins/database-record';
import { HasActivityLogs } from '../mixins/has-activity-logs';
import { AgencyBlocAgent } from './agency-bloc-agent';
import { AgencyBlocEnties } from './agency-bloc-entities';
import { AgencyBlocGroup } from './agency-bloc-group';
import { Agreement, ApplicantHealth, BasicContact, ClientProfile, HealthQuestionnaire, Meetings, SimpleForm } from './form-submission-types';

type FormFactoryFunction<T> = (data: any) => T;
/**
 * A database record containing FormSubmission data sent by a customer or Agent
 */
export class FormSubmission<FormSubmissionType> extends DatabaseRecord(HasActivityLogs()) {
  public partner_id = 0;
  public user_id = 0;
  public agency_bloc_agent_id = 0;
  public webinar_event_id = 0;

  /**
   * Has a Wolf employee archived the record?
   */
  public wolf_archived = false;

  /**
   * Has the Agent archived the record?
   */
  public agent_archived = false;

  /**
   * Is this marked urgent for Wolf employees?
   */
  public wolf_urgent = false;

  /**
   * Is this marked urgent for Agents?
   */
  public agent_urgent = false;

  /**
   * Did they attend the webinar?
   */
  public attended_webinar = false;

  /**
   * The reason this FormSubmission was archived urgently
   */
  public urgent_reason?: string;

  /**
   * The name of the form that made the submission
   */
  public form: FormSubmissionNameType = FormSubmissionNames[0];

  /**
   * Agent's full name
   */
  public referred_by = '';

  /**
   * The AgencyBloc entity ID, if the record was created on AgencyBloc
   */
  public entityID = 0;

  /**
   * Is this a AgencyBlocIndividual, AgencyBlocAgent, or AgencyBlocGroup?
   */
  public entityType: string = AgencyBlocEnties[0];

  /**
   * The user's FormSubmission payload
   */
  public data?: FormSubmissionType;

  /**
   * The user's partner during the form submission
   */
  public partner?: FormSubmission<FormSubmissionType>;

  /**
   * Files uploaded by the user
   */
  public files: string[] = [];

  /**
   * The agent that this is associated with
   */
  public agent?: AgencyBlocAgent;

  /**
   * Is this record checked in the UI?
   */
  public checked = false;

  /**
   * The AgencyBlocGroup that this record is associated with
   */
  public group?: AgencyBlocGroup;

  public meeting?: Meeting = undefined;

  /**
   * If record for applicant's partner
   */
  public secondary_data: boolean=false;

  private _full_name = '';
  private _email = '';

  public set full_name(value: string) {
    this._full_name = value;
  }

  public get full_name(): string {
    return this._full_name;
  }

  public set email(value: string) {
    this._email = value;
  }

  public get email(): string {
    return this._email;
  }

  constructor(record?: FormSubmission<FormSubmissionType>) {
    super(record);
    Object.assign(this, record);
    if (record) {
      if (record.partner) this.partner = new FormSubmission(record.partner);
      if (record.agent) this.agent = new AgencyBlocAgent(record.agent);
      if (record?.meeting) this.meeting = new Meeting(record.meeting);
      if (record.created_at) this.created_at = new Date(record.created_at);
      if (record.updated_at) this.updated_at = new Date(record.updated_at);
      if (record.deleted_at) this.deleted_at = new Date(record.deleted_at);
      if (record.data) this.data = this.toType();
    }
  }

  /**
   * Create Form Instance
   * @param {FormSubmissionType | undefined} data - The Form Data
   * @returns {T}
   */
  private createFormInstance<T>(createForm: FormFactoryFunction<T>, data: FormSubmissionType | undefined): T {
    return createForm(data);
  }

  /**
   * Convert the data to the correct type
   * @returns {T}
   */
  public toType<T extends FormSubmissionType>(): T {
    const formFactoryMapping: Record<FormSubmissionNameType, FormFactoryFunction<T>> = {
      health_questionnaire: (data: HealthQuestionnaire) => new HealthQuestionnaire(data) as T,
      additional_medication: (data: ApplicantHealth) => new ApplicantHealth(data) as T,
      client_profile: (data: ClientProfile) => new ClientProfile(data) as T,
      referral: (data: SimpleForm) => new SimpleForm(data) as T,
      medications: (data: SimpleForm) => new SimpleForm(data) as T,
      education_consult: (data: SimpleForm) => new SimpleForm(data) as T,
      application_consult: (data: SimpleForm) => new SimpleForm(data) as T,
      application_questionnaire: (data: SimpleForm) => new SimpleForm(data) as T,
      application_health: (data: SimpleForm) => new SimpleForm(data) as T,
      application_health_stc: (data: SimpleForm) => new SimpleForm(data) as T,
      under_writing: (data: SimpleForm) => new SimpleForm(data) as T,
      advisor_login: (data: SimpleForm) => new SimpleForm(data) as T,
      pdf_download: (data: SimpleForm) => new SimpleForm(data) as T,
      advisor_contact: (data: SimpleForm) => new SimpleForm(data) as T,
      agreement: (data: Agreement) => new Agreement(data) as T,
      additional_agreement_info: (data: Agreement) => new Agreement(data) as T,
      additional_agreement_info_advisor: (data: Agreement) => new Agreement(data) as T,
      additional_agreement_info_associate: (data: Agreement) => new Agreement(data) as T,
      additional_agreement_info_office_professional: (data: Agreement) => new Agreement(data) as T,
      webinar: (data: SimpleForm) => new SimpleForm(data) as T,
      birthdays: (data: SimpleForm) => new SimpleForm(data) as T,
      articles: (data: SimpleForm) => new SimpleForm(data) as T,
      basic_contact: (data: BasicContact) => new BasicContact(data) as T,
      additional_information: (data: SimpleForm) => new SimpleForm(data) as T,
      additional_info_advisor: (data: SimpleForm) => new SimpleForm(data) as T,
      additional_info_group: (data: SimpleForm) => new SimpleForm(data) as T,
      //AP Meetings
      'education-meeting': (data: Meetings) => new Meetings(data) as T,
      'education-solutions-meeting': (data: Meetings) => new Meetings(data) as T,
      'solutions-meeting': (data: Meetings) => new Meetings(data) as T,
      'application-meeting': (data: Meetings) => new Meetings(data) as T,
      'health-call': (data: Meetings) => new Meetings(data) as T,
      //AA Meetings
      'benefit-design-meeting': (data: Meetings) => new Meetings(data) as T,
      'policy-delivery-meeting': (data: Meetings) => new Meetings(data) as T,
      //AA Meetings
      'claims-review-meeting': (data: Meetings) => new Meetings(data) as T,
      'policy-review-meeting': (data: Meetings) => new Meetings(data) as T,
      'rate-increase-review-meeting': (data: Meetings) => new Meetings(data) as T,
      //AG Meetings
      'advisor-intro-meeting': (data: Meetings) => new Meetings(data) as T,
      'advisor-coaching-call': (data: Meetings) => new Meetings(data) as T,
      //GP Meetings
      'agreement-meeting': (data: Meetings) => new Meetings(data) as T,
      'onboarding-meeting': (data: Meetings) => new Meetings(data) as T,
      'webinar-planning-meeting': (data: Meetings) => new Meetings(data) as T,
      'webinar-meeting': (data: Meetings) => new Meetings(data) as T,
      'group-education-meeting': (data: Meetings) => new Meetings(data) as T,
      //NCS Meetings
      'non-client-services-meeting': (data: Meetings) => new Meetings(data) as T,
      //Other Meetings
      'med-supp-education-meeting': (data: Meetings) => new Meetings(data) as T,
      'strategic-planning-meeting': (data: Meetings) => new Meetings(data) as T,
      //ACS Meetings
      'advisor-support-meeting': (data: Meetings) => new Meetings(data) as T,
      'claims-advocacy-meeting': (data: Meetings) => new Meetings(data) as T,
      'care-managment-meeting': (data: Meetings) => new Meetings(data) as T,
      //Meeting Cancellation
      cancellations: (data: Meetings) => new Meetings(data) as T,
    };
    const createForm = formFactoryMapping[this.form];
    if (createForm) {
      return this.createFormInstance(createForm, this.data);
    } else {
      throw new Error(`FormSubmissionType '${this.form}' not implemented`);
    }
  }

  public createFormGroup(): FormGroup {
    const name = this.full_name.split(' ');
    const first_name = name[0];
    const last_name = name[name.length - 1];
    const middle_initial = name.length > 2 ? name[1] : '';

    return new FormGroup({
      firstName: new FormControl(first_name, []),
      middleInitial: new FormControl(middle_initial, []),
      lastName: new FormControl(last_name, []),
      email: new FormControl(this.email, [Validators.email]),
      leadDate: new FormControl(this.created_at),
      birthDate: new FormControl(''),
      gender: new FormControl('', []),
      maritalStatus: new FormControl('', []),
      status: new FormControl('', []),
      type: new FormControl('', []),
      projectCode: new FormControl('', []),
    });
  }
}

/**
 * The names of the forms that can be submitted
 */
export const FormSubmissionNames = [
  'health_questionnaire',
  'application_question',
  'agreement',
  'additional_medication',
  'webinar',
  'client_profile',
  'app_consult',
  'ed_consult',
  'medicare',
  'referral',
  'advisor_login',
  'downloads',
  'advisor_contact',
  'under_writing',
  'basic_contact',
];

/**
 * The key of submitted form
 */
export const SubmittedFormDataKeys = ['applicant'];

/**
 * The types of the forms that can be submitted
 */
export type FormSubmissionNameType = (typeof FormSubmissionNames)[number];
